import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import GlobalSearch from '../../components/GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import UserDropdownMenu from '../../components/UserDropdownMenu';
import ImpersonationBanner from '../../components/guards/ImpersonationBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  CreditCard, 
  Settings, 
  FileText, 
  Globe, 
  LogOut, 
  Search, 
  Bell, 
  Menu,
  X,
  Crown,
  Shield,
  Database,
  Activity,
  MessageSquare,
  BookOpen,
  Mail,
  Calendar,
  ChevronDown,
  ChevronRight,
  Coins,
  Briefcase,
} from 'lucide-react';

const UltraLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/app/ultra/dashboard',
      description: 'Sistem genel görünümü'
    },
    {
      label: 'Organizasyonlar',
      icon: Building2,
      path: '/app/ultra/orgs',
      description: 'Şirketler ve hesaplar'
    },
    {
      label: 'Kullanıcılar',
      icon: Users,
      path: '/app/ultra/users',
      description: 'Tüm sistem kullanıcıları'
    },
    {
      label: 'Planlar',
      icon: Crown,
      path: '/app/ultra/plans',
      description: 'Üyelik planları yönetimi'
    },
    {
      label: 'Abonelikler',
      icon: CreditCard,
      path: '/app/ultra/subscriptions',
      description: 'Aktif abonelikler'
    },
    {
      label: 'Denetim Logları',
      icon: Shield,
      path: '/app/ultra/audit',
      description: 'Sistem aktivite logları'
    },
    {
      label: 'Brief Yönetimi',
      icon: FileText,
      path: '/app/ultra/briefs',
      description: 'Tüm platform brief\'leri'
    },
    {
      label: 'Tedarikçiler',
      icon: Database,
      path: '/app/ultra/suppliers',
      description: 'Tedarikçi hesapları'
    },
    {
      label: 'Gelen Sorgular',
      icon: MessageSquare,
      path: '/app/ultra/inquiries',
      description: 'Brief ve iletişim talepleri'
    },
    {
      label: 'Exhibitor Profiles',
      icon: Building2,
      path: '/app/ultra/exhibitor-profiles',
      description: 'Katılımcı profil yönetimi'
    },
    {
      label: 'Webhook\'lar',
      icon: Globe,
      path: '/app/ultra/webhooks',
      description: 'API webhook yönetimi'
    },
    {
      label: 'Ana Sayfa İçerik',
      icon: Globe,
      path: '/app/ultra/cms',
      description: 'Ana sayfa görseller ve metinler'
    },
    {
      label: 'Fuar Veritabanı',
      icon: Calendar,
      path: '/app/ultra/fairs',
      description: 'Uluslararası fuar ve etkinlik yönetimi'
    },
    {
      label: 'Organizatörler',
      icon: Building2,
      path: '/app/ultra/organizers',
      description: 'Fuar organizatörleri yönetimi'
    },
    {
      label: 'Dropdown Yönetimi',
      icon: Database,
      path: '/app/ultra/dropdowns',
      description: 'Ülke, şehir, para birimi, kod'
    },
    {
      label: 'Kurallar',
      icon: BookOpen,
      path: '/app/ultra/rules',
      description: 'Platform kuralları'
    },
    {
      label: 'Kredi Yönetimi',
      icon: Coins,
      path: '/app/ultra/credits',
      description: 'Kredi paketleri ve cüzdanlar'
    },
    {
      label: 'Öne Çıkarma & Rozetler',
      icon: Shield,
      path: '/app/ultra/featured-listings',
      description: 'Featured listing ve verified badge yönetimi'
    },
    {
      label: 'Kullanıcı Yorumları',
      icon: MessageSquare,
      path: '/app/ultra/testimonials',
      description: 'Kullanıcı referans yönetimi'
    },
    {
      label: 'E-posta Yönetimi',
      icon: Mail,
      path: '/app/ultra/email-management',
      description: 'Sistem e-postaları kataloğu ve şablonları'
    },
    {
      label: 'Email Scheduler',
      icon: Mail,
      path: '/app/ultra/scheduler',
      description: 'Otomatik e-posta kampanyası'
    },
    {
      label: 'Platform Özellikleri',
      icon: BookOpen,
      path: '/app/ultra/platform-features',
      description: 'Tüm platform özelliklerinin dokümantasyonu'
    },
    {
      label: 'Freelancer Kategorileri',
      icon: Briefcase,
      path: '/app/ultra/freelancers',
      description: 'Freelancer kategori ve komisyon yönetimi'
    },
    {
      label: 'İş İlanları',
      icon: FileText,
      path: '/app/ultra/job-posts',
      description: 'Tüm iş ilanlarını yönet'
    },
    {
      label: 'Sistem Ayarları',
      icon: Settings,
      path: '/app/ultra/settings',
      description: 'Genel sistem konfigürasyonu'
    }
  ];

  return (
    <>
      <GlobalSearch />
      <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
                      lg:translate-x-0 flex flex-col fixed inset-y-0 left-0 lg:static w-64 
                      bg-white shadow-lg z-30 transition-transform duration-300 ease-in-out`}
                      data-testid="ultra-sidebar">
        
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Ultra Admin</h1>
                <p className="text-xs text-gray-500">Sistem Yönetimi</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {/* Menü Arama */}
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={menuSearch}
              onChange={e => setMenuSearch(e.target.value)}
              placeholder="Menüde ara..."
              className="w-full pl-8 pr-8 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-gray-400"
            />
            {menuSearch && (
              <button onClick={() => setMenuSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Nav Items */}
          <div className="space-y-1">
            {sidebarItems
              .filter(item =>
                !menuSearch ||
                item.label.toLowerCase().includes(menuSearch.toLowerCase()) ||
                item.description.toLowerCase().includes(menuSearch.toLowerCase())
              )
              .map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors group ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                    title={item.description}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      {menuSearch ? (
                        <span className="truncate" dangerouslySetInnerHTML={{
                          __html: item.label.replace(
                            new RegExp(`(${menuSearch})`, 'gi'),
                            '<mark class="bg-yellow-100 text-yellow-800 rounded px-0.5">$1</mark>'
                          )
                        }} />
                      ) : (
                        <span className="truncate">{item.label}</span>
                      )}
                    </div>
                  </NavLink>
                );
              })}
            {menuSearch && sidebarItems.filter(item =>
              item.label.toLowerCase().includes(menuSearch.toLowerCase()) ||
              item.description.toLowerCase().includes(menuSearch.toLowerCase())
            ).length === 0 && (
              <div className="py-6 text-center text-xs text-gray-400">
                <Search className="w-6 h-6 mx-auto mb-2 opacity-40" />
                "{menuSearch}" için sonuç bulunamadı
              </div>
            )}
          </div>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="w-full text-gray-600 border-gray-300 hover:bg-gray-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Çıkış Yap
          </Button>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Impersonation Banner */}
        <ImpersonationBanner />

        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200" data-testid="ultra-topbar">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Left: Mobile menu + Search */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="search"
                  placeholder="Arama... (Cmd+K)"
                  className="pl-10 pr-4 py-2 w-64 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      // TODO: Open search modal
                    }
                  }}
                />
              </div>
            </div>

            {/* Right: Notifications + User menu */}
            <div className="flex items-center space-x-3">
              <button className="relative p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                <Bell className="w-5 h-5" />
                <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-[18px] h-[18px] rounded-full flex items-center justify-center p-0">
                  3
                </Badge>
              </button>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 hidden sm:flex">
                <Database className="w-3 h-3 mr-1" />
                Ultra Admin
              </Badge>
              <UserDropdownMenu dashboardRoute="/app/ultra/dashboard" />
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 p-3 sm:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
    </>
  );
};

export default UltraLayout;